import React, { useEffect, useMemo, useState } from 'react';
import { Save, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

const num = (v) => (v === '' || v == null ? null : parseFloat(v));
const cond = (m) => (m.category === 'used' ? 'used' : 'new');
const inputCls = 'w-full bg-brand-bg border border-white/10 focus:border-brand-primary text-white px-2 py-2 outline-none text-sm';

const FleetRow = ({ m, onSaved }) => {
    const [f, setF] = useState({
        rentable: !!m.rentable, rental_daily: m.rental_daily ?? '', rental_weekly: m.rental_weekly ?? '',
        rental_deposit: m.rental_deposit ?? '', rental_specs: m.rental_specs || '',
    });
    const [busy, setBusy] = useState(false);
    const upd = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
    const save = async () => {
        setBusy(true);
        try {
            const { data } = await api.put(`/admin/machines/${m.id}/rental`, {
                ...f, rental_daily: num(f.rental_daily), rental_weekly: num(f.rental_weekly), rental_deposit: num(f.rental_deposit),
            });
            toast.success(`${m.name} ${data.rentable ? 'is on the Rental page' : 'saved'}`);
            onSaved(m.id, data);
        } catch (e) {
            toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Check the prices and try again');
        }
        setBusy(false);
    };
    return (
        <div className={`p-4 border-b border-white/10 ${f.rentable ? 'bg-brand-primary/5' : ''}`} data-testid={`fleet-row-${m.id}`}>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-center">
                <label className="lg:col-span-4 flex items-center gap-3 cursor-pointer min-w-0">
                    <input type="checkbox" checked={f.rentable} onChange={upd('rentable')} className="h-4 w-4 accent-[#F7941D]" data-testid={`fleet-rentable-${m.id}`} />
                    {m.image_url && <img src={m.image_url} alt="" className="w-10 h-10 object-contain bg-white flex-shrink-0" />}
                    <span className="min-w-0">
                        <span className="block text-white font-bold truncate">{m.name}</span>
                        <span className={`label-caps text-[10px] ${cond(m) === 'used' ? 'text-amber-400' : 'text-emerald-400'}`}>{cond(m)}</span>
                    </span>
                </label>
                {[['rental_daily', 'Day $'], ['rental_weekly', 'Week $'], ['rental_deposit', 'Deposit $']].map(([k, l]) => (
                    <label key={k} className="lg:col-span-2 text-xs text-white/50">{l}
                        <input type="number" min={0} value={f[k]} onChange={upd(k)} className={inputCls} data-testid={`fleet-${k.replace('rental_', '')}-${m.id}`} />
                    </label>
                ))}
                <div className="lg:col-span-2 flex lg:justify-end">
                    <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-60 text-brand-ink font-bold px-4 py-2 label-caps text-xs" data-testid={`fleet-save-${m.id}`}>
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                    </button>
                </div>
            </div>
            {f.rentable && (
                <textarea rows={2} value={f.rental_specs} onChange={upd('rental_specs')} className={`${inputCls} mt-3`} data-testid={`fleet-specs-${m.id}`}
                    placeholder="Rental specs shown to renters, e.g. Includes 20 ft hose, gun and 2 nozzles · needs 185 CFM compressor · 30 lb hopper" />
            )}
        </div>
    );
};

const RentalFleetAdmin = () => {
    const [machines, setMachines] = useState([]);
    const [q, setQ] = useState('');
    const [show, setShow] = useState('all');
    useEffect(() => { api.get('/admin/machines').then((r) => setMachines(r.data.filter((m) => m.machine_type !== 'accessory'))); }, []);
    const onSaved = (id, data) => setMachines((ms) => ms.map((m) => (m.id === id ? { ...m, ...data } : m)));
    const list = useMemo(() => machines
        .filter((m) => (show === 'rentable' ? m.rentable : show === 'all' ? true : cond(m) === show))
        .filter((m) => m.name.toLowerCase().includes(q.toLowerCase()))
        .sort((a, b) => Number(!!b.rentable) - Number(!!a.rentable) || a.name.localeCompare(b.name)), [machines, q, show]);
    const count = machines.filter((m) => m.rentable).length;

    return (
        <div className="mb-12" data-testid="admin-rental-fleet">
            <h2 className="font-display font-black uppercase text-3xl mb-2">Rental fleet</h2>
            <p className="text-white/50 text-sm mb-6">Tick a machine (new or used), set its day / week / deposit prices and specs, then Save — it appears on the Rental page right away. <span data-testid="fleet-count">{count}</span> machine{count === 1 ? '' : 's'} for rent. The deposit is paid online when the customer books.</p>
            <div className="flex flex-col md:flex-row gap-3 mb-4">
                <div className="relative flex-1"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search machines" className={`${inputCls} pl-9 py-3`} data-testid="fleet-search" /></div>
                <div className="flex gap-2" data-testid="fleet-filters">
                    {[['all', 'All'], ['rentable', 'For rent'], ['new', 'New'], ['used', 'Used']].map(([v, l]) => (
                        <button key={v} onClick={() => setShow(v)} data-testid={`fleet-filter-${v}`}
                            className={`px-3 py-2 label-caps text-xs ${show === v ? 'bg-brand-primary text-brand-ink' : 'border border-white/10 text-white/60 hover:text-white'}`}>{l}</button>
                    ))}
                </div>
            </div>
            <div className="border border-white/10" data-testid="fleet-list">
                {list.length === 0 && <div className="p-6 text-white/50">No machines match.</div>}
                {list.map((m) => <FleetRow key={m.id} m={m} onSaved={onSaved} />)}
            </div>
        </div>
    );
};

export default RentalFleetAdmin;
